import {
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
  type TouchEvent,
} from 'react';
import { Link, useNavigate } from 'react-router';
import type { BodyBlock, BodyRichText } from '@shared/domain/card.ts';
import { isPriorityTag } from '@shared/domain/priority.ts';
import { simpleStatus } from '@shared/domain/flow.ts';
import type { StatusGroup } from '@shared/domain/status.ts';
import type { PersonInfo } from '../lib/people.ts';
import { store } from '../state/store.ts';

/** Re-renderiza cada `intervalMs` (cronómetro, "hace X"). */
export function useNow(intervalMs = 1000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * Escritorio e iPad horizontal: Mis tareas y Todas las tarjetas se ven como Kanban (D-043).
 * Debe coincidir con la media query de `.app-board` en styles.css.
 */
export const WIDE_QUERY = '(min-width: 1024px) and (orientation: landscape), (min-width: 1280px)';

function subscribeWide(onChange: () => void) {
  const query = window.matchMedia(WIDE_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

export function useWideLayout(): boolean {
  return useSyncExternalStore(subscribeWide, () => window.matchMedia(WIDE_QUERY).matches);
}

const PULL_THRESHOLD = 80;

/** Deslizar hacia abajo desde el tope de la lista sincroniza con Notion. */
export function usePullToRefresh() {
  const [pull, setPull] = useState(0);
  const startY = useRef<number | null>(null);
  const handlers = {
    onTouchStart(e: TouchEvent) {
      startY.current = window.scrollY <= 0 ? (e.touches[0]?.clientY ?? null) : null;
    },
    onTouchMove(e: TouchEvent) {
      if (startY.current === null) return;
      setPull(Math.max(0, Math.min(140, (e.touches[0]?.clientY ?? 0) - startY.current)));
    },
    onTouchEnd() {
      if (pull >= PULL_THRESHOLD) void store.refresh('gesto');
      startY.current = null;
      setPull(0);
    },
  };
  const hint =
    pull > 0 ? (
      <p className="pull-hint" style={{ height: pull / 2 }}>
        {pull >= PULL_THRESHOLD ? 'Suelta para actualizar' : 'Desliza para actualizar'}
      </p>
    ) : null;
  return { handlers, hint };
}

/** Vuelve a la pantalla anterior; si se entró directo, a Mis tareas. */
export function BackLink() {
  const navigate = useNavigate();
  return (
    <p>
      <Link
        to="/"
        onClick={(e) => {
          const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
          if (idx > 0) {
            e.preventDefault();
            void navigate(-1);
          }
        }}
      >
        ← Volver
      </Link>
    </p>
  );
}

/** La única acción de horas de cada fila: la hoja ofrece iniciar el cronómetro o registrar. */
export function HoursButton({
  cardId,
  name,
  running,
}: {
  cardId: string;
  name: string;
  running: boolean;
}) {
  return (
    <button
      type="button"
      className={`hours-button${running ? ' hours-button-running' : ''}`}
      aria-label={`Horas en ${name}`}
      onClick={() => store.openHoursSheet({ cardId, withTimer: true })}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </button>
  );
}

/** Hoja inferior (diálogo modal): se cierra con ×, Escape o tocando fuera. */
export function SheetDialog({
  title,
  subtitle,
  onClose,
  testId,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  testId?: string;
  children: ReactNode;
}) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dialog.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        ref={dialog}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <div className="sheet-head">
          <div>
            <h2 id={titleId}>{title}</h2>
            {subtitle !== undefined && <p className="muted small">{subtitle}</p>}
          </div>
          <button type="button" className="icon-button" aria-label="Cerrar" onClick={onClose}>
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Status con su nombre simple (Por hacer, Urgente…) o, si no tiene, el de Notion. */
export function StatusChip({ status, group }: { status: string; group: StatusGroup }) {
  const simple = simpleStatus(status);
  return (
    <span className={`chip status-chip status-${group}`} title={simple ? status : undefined}>
      {simple ?? status}
    </span>
  );
}

export function TagChip({ tag }: { tag: string }) {
  const priority = isPriorityTag(tag)
    ? tag.trim().toUpperCase().replace('PRIORIDAD ', '').toLowerCase()
    : null;
  return <span className={`chip${priority ? ` chip-prio-${priority}` : ''}`}>{tag}</span>;
}

/**
 * Iniciales con un color estable por persona; sin nombre, "?" con borde punteado. `decorative`
 * cuando el nombre ya está escrito al lado (no se repite para lectores de pantalla).
 */
export function Avatar({
  person,
  decorative = false,
}: {
  person: PersonInfo;
  decorative?: boolean;
}) {
  return (
    <span
      className={`avatar${person.named ? '' : ' avatar-unnamed'}`}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative || undefined}
      title={person.label}
      aria-label={decorative ? undefined : person.label}
      style={{ '--avatar-hue': person.hue } as CSSProperties}
    >
      {person.initials}
    </span>
  );
}

/** Avatar y nombre (detalle de tarjeta). */
export function PersonChip({ person }: { person: PersonInfo }) {
  return (
    <span className={`person-chip${person.named ? '' : ' muted'}`}>
      <Avatar person={person} decorative /> {person.label}
    </span>
  );
}

export function RichText({ parts }: { parts: readonly BodyRichText[] }) {
  return (
    <>
      {parts.map((p, i) => {
        let node: ReactNode = p.text;
        if (p.code) node = <code>{node}</code>;
        if (p.bold) node = <strong>{node}</strong>;
        if (p.italic) node = <em>{node}</em>;
        if (p.strikethrough) node = <s>{node}</s>;
        if (p.underline) node = <u>{node}</u>;
        if (p.href) {
          node = (
            <a href={p.href} target="_blank" rel="noreferrer noopener">
              {node}
            </a>
          );
        }
        return <span key={i}>{node}</span>;
      })}
    </>
  );
}

const TEXT_TAGS = {
  paragraph: 'p',
  heading_1: 'h3',
  heading_2: 'h4',
  heading_3: 'h5',
  quote: 'blockquote',
  callout: 'aside',
  toggle: 'p',
} as const;

function groupLists(
  blocks: readonly BodyBlock[],
): (BodyBlock | { type: 'list'; ordered: boolean; items: BodyBlock[] })[] {
  const out: (BodyBlock | { type: 'list'; ordered: boolean; items: BodyBlock[] })[] = [];
  for (const b of blocks) {
    const ordered = b.type === 'numbered_list_item';
    if (b.type === 'bulleted_list_item' || ordered) {
      const last = out[out.length - 1];
      if (last && last.type === 'list' && last.ordered === ordered) last.items.push(b);
      else out.push({ type: 'list', ordered, items: [b] });
    } else {
      out.push(b);
    }
  }
  return out;
}

/** Cuerpo de la tarjeta en solo lectura: tabla, texto enriquecido, enlaces e imágenes. */
export function BodyView({ blocks }: { blocks: readonly BodyBlock[] }) {
  return (
    <div className="body-view">
      {groupLists(blocks).map((b, i) => {
        if (b.type === 'list') {
          const Tag = b.ordered ? 'ol' : 'ul';
          return (
            <Tag key={i}>
              {b.items.map((item, j) =>
                'rich_text' in item ? (
                  <li key={j}>
                    <RichText parts={item.rich_text} />
                    {item.children && <BodyView blocks={item.children} />}
                  </li>
                ) : null,
              )}
            </Tag>
          );
        }
        switch (b.type) {
          case 'table':
            return (
              <div key={i} className="table-wrap">
                <table className="body-table">
                  <tbody>
                    {b.rows.map((row, r) => (
                      <tr key={r}>
                        {row.map((cell, c) => {
                          const header =
                            (b.has_row_header && c === 0) || (b.has_column_header && r === 0);
                          return header ? (
                            <th key={c} scope={c === 0 ? 'row' : 'col'}>
                              <RichText parts={cell} />
                            </th>
                          ) : (
                            <td key={c}>
                              <RichText parts={cell} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'image':
            return (
              <figure key={i}>
                <img
                  src={b.url}
                  alt={b.caption.map((c) => c.text).join('') || 'Imagen de la tarjeta'}
                  loading="lazy"
                />
                {b.caption.length > 0 && (
                  <figcaption>
                    <RichText parts={b.caption} />
                  </figcaption>
                )}
              </figure>
            );
          case 'bookmark':
            return (
              <p key={i}>
                <a href={b.url} target="_blank" rel="noreferrer noopener">
                  {b.caption.length > 0 ? b.caption.map((c) => c.text).join('') : b.url}
                </a>
              </p>
            );
          case 'divider':
            return <hr key={i} />;
          case 'unsupported':
            return (
              <p key={i} className="muted small">
                (Contenido "{b.original_type}" no disponible en Tablero; ábrelo en Notion.)
              </p>
            );
          case 'to_do':
            return (
              <p key={i}>
                <input
                  type="checkbox"
                  checked={Boolean(b.checked)}
                  readOnly
                  disabled
                  aria-label="Tarea"
                />{' '}
                <RichText parts={b.rich_text} />
              </p>
            );
          default: {
            if (!('rich_text' in b)) return null;
            const Tag = TEXT_TAGS[b.type as keyof typeof TEXT_TAGS] ?? 'p';
            return (
              <Tag key={i}>
                <RichText parts={b.rich_text} />
                {b.children && <BodyView blocks={b.children} />}
              </Tag>
            );
          }
        }
      })}
    </div>
  );
}
